<template>
  <div class="flex flex-col gap-6">
    <p v-if="!isValidTaskId" class="text-sm text-destructive">Invalid task id.</p>
    <p v-else-if="tasksStore.isLoading && !task" class="text-sm text-muted-foreground">Loading task...</p>
    <p v-else-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </p>
    <div v-else-if="task" class="flex flex-col gap-5">
      <header class="px-1">
        <p class="mb-2 font-mono text-xs uppercase tracking-wide text-muted-foreground">
          {{ taskReference }}
        </p>
        <h1 class="max-w-5xl break-words text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {{ taskTitle }}
        </h1>
      </header>

      <TaskActionBar
        :task="task"
        context="task-show"
        :readonly="isTaskReadonly"
        :is-creating-checklist="isCreatingChecklistFromFeatureMenu"
        @create-checklist="createChecklistFromFeatureMenu"
        @review-process-updated="onReviewProcessUpdated"
        @tags-enabled="enableTagsFeature"
      />

      <ReviewProcessSetupCard
        v-if="shouldShowReviewProcessSetupCard"
        :review-process-id="reviewProcess?.id ?? null"
        :reviewable-type="taskObjectType"
        :reviewable-id="task.id"
        mode="auto"
        title="Review Required"
        @updated="onReviewProcessUpdated"
        @created="onReviewProcessUpdated"
        @removed="onReviewProcessUpdated"
      />

      <div class="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.7fr)_minmax(22rem,0.85fr)]">
        <div class="flex min-w-0 flex-col gap-5">
          <TaskTitleDescriptionCard :task="task" context="task-show" :readonly="isTaskReadonly" :show-title="false" />

          <div v-show="shouldShowChecklistsCard">
            <ChecklistsCard
              ref="checklistsCard"
              :checklistable-id="task.id"
              :checklistable-type="taskObjectType"
              :is-not-executable="isChecklistNotExecutable"
              :readonly="isTaskReadonly"
              @count-changed="onChecklistCountChanged"
            />
          </div>

          <NotesCard :id="task.id" :noteable="taskObjectType" type="comment" :readonly="areTaskNotesReadonly" />
          <EventLogCard :eventable_id="task.id" :eventable_type="taskObjectType" context="task-show-event-logs" minimzed />
        </div>

        <div class="flex min-w-0 flex-col gap-5 xl:sticky xl:top-5">
          <TaskStatusFlowCard :task="task" context="task-show" :readonly="isStatusFlowReadonly" />

          <UploadsCard :uploadable-id="task.id" :uploadable-type="taskObjectType" context="task-show-uploads" :readonly="isTaskReadonly">
            <template #title> Task Attachments </template>
          </UploadsCard>

          <div v-if="shouldShowTagsCard">
            <TaskTagsCard ref="taskTagsCard" :task="task" context="task-show-tags" :readonly="isTaskReadonly" @changed="onTaskTagsChanged" @count-changed="onTagsCountChanged" />
          </div>
        </div>
      </div>
    </div>
    <p v-else class="text-sm text-muted-foreground">Task not found.</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import ChecklistsCard from '@Checklists/components/checklists/ChecklistsCard.vue'
import NotesCard from '@Notes/components/notes/NotesCard.vue'
import UploadsCard from '@Uploadable/components/uploads/UploadsCard.vue'
import EventLogCard from '@Utils/components/event-logs/EventLogCard.vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue-sonner'
import { useAuthStore } from '../../../../../../resources/js/stores/auth.js'
import TaskActionBar from '../components/tasks/TaskActionBar.vue'
import TaskStatusFlowCard from '../components/tasks/TaskStatusFlowCard.vue'
import TaskTagsCard from '../components/tasks/TaskTagsCard.vue'
import TaskTitleDescriptionCard from '../components/tasks/TaskTitleDescriptionCard.vue'
import { hasTaskReviewProcess, resolveTaskReviewProcess } from '../utils/taskReviewProcess.js'
import { useTasksStore } from '../stores/tasksStore.js'
import ReviewProcessSetupCard from '@Review-process/components/review-process/ReviewProcessSetupCard.vue'

const route = useRoute()
const authStore = useAuthStore()
const tasksStore = useTasksStore('task-show')
const checklistsCard = ref(null)
const taskTagsCard = ref(null)
const checklistCount = ref(0)
const tagsCount = ref(0)
const isTagsFeatureEnabled = ref(false)
const isCreatingChecklistFromFeatureMenu = ref(false)

const taskId = computed(() => Number(route.params.task ?? 0))
const isValidTaskId = computed(() => Number.isInteger(taskId.value) && taskId.value > 0)
const task = computed(() => {
  if (!isValidTaskId.value) {
    return null
  }

  return tasksStore.getItemById(taskId.value) ?? null
})
const taskTitle = computed(() => {
  const title = typeof task.value?.title === 'string' ? task.value.title.trim() : ''

  return title !== '' ? title : 'Untitled task'
})
const taskReference = computed(() => {
  const id = Number(task.value?.id)

  return Number.isInteger(id) && id > 0 ? `Task #${id}` : 'Task'
})
const taskObjectType = computed(() => task.value?.object_type ?? '')
const normalizedTaskStatus = computed(() => {
  if (typeof task.value?.status === 'string') {
    return task.value.status
  }

  if (task.value?.status && typeof task.value.status === 'object' && 'value' in task.value.status) {
    return String(task.value.status.value)
  }

  return ''
})
const isChecklistNotExecutable = computed(() => normalizedTaskStatus.value !== 'in_progress')
const readonlyTaskStatuses = ['blocked', 'completed']
const currentUserId = computed(() => normalizeUserId(authStore.currentUser?.id))
const assignedToUserId = computed(() => normalizeUserId(task.value?.assigned_to_user_id))
const isTaskAssignedToAnotherUser = computed(() => assignedToUserId.value !== null && assignedToUserId.value !== currentUserId.value)
const isTaskStatusReadonly = computed(() => readonlyTaskStatuses.includes(normalizedTaskStatus.value))
const isTaskReadonly = computed(() => isTaskStatusReadonly.value || isTaskAssignedToAnotherUser.value)
const isStatusFlowReadonly = computed(() => isTaskAssignedToAnotherUser.value)
const areTaskNotesReadonly = computed(() => isTaskStatusReadonly.value)
const cachedChecklistCount = computed(() => Number(task.value?.cached?.checklists_count ?? 0))
const cachedTagsCount = computed(() => Number(task.value?.cached?.tags_count ?? 0))
const hasChecklistFeature = computed(() => checklistCount.value > 0)
const hasTagsFeature = computed(() => tagsCount.value > 0 || isTagsFeatureEnabled.value)
const shouldShowChecklistsCard = computed(() => hasChecklistFeature.value)
const shouldShowTagsCard = computed(() => hasTagsFeature.value)
const errorMessage = computed(() => {
  const message = tasksStore.error?.response?.data?.message

  if (typeof message === 'string' && message.trim() !== '') {
    return message
  }

  if (tasksStore.error && !tasksStore.isLoading) {
    return 'Unable to load task.'
  }

  return ''
})
const reviewProcess = computed(() => resolveTaskReviewProcess(task.value))
const shouldShowReviewProcessSetupCard = computed(() => hasTaskReviewProcess(task.value))

const fetchTask = async () => {
  if (!isValidTaskId.value) {
    return
  }

  try {
    await tasksStore.fetchItemById(taskId.value)
  } catch {
    // The generic store keeps the error in state for display.
  }
}

onMounted(() => {
  fetchTask()
})

watch(taskId, () => {
  fetchTask()
})

watch(
  () => task.value?.id,
  () => {
    checklistCount.value = cachedChecklistCount.value
    tagsCount.value = cachedTagsCount.value
    isTagsFeatureEnabled.value = false
  },
  { immediate: true },
)

watch(cachedChecklistCount, (count) => {
  if (count > 0) {
    checklistCount.value = count
  }
})

watch(cachedTagsCount, (count) => {
  if (count > 0) {
    tagsCount.value = count
  }
})

const onReviewProcessUpdated = async () => {
  await fetchTask()
}

const onChecklistCountChanged = (count) => {
  const normalizedCount = Number(count)

  checklistCount.value = Number.isInteger(normalizedCount) && normalizedCount > 0 ? normalizedCount : 0
}

const onTagsCountChanged = (count) => {
  const normalizedCount = Number(count)

  tagsCount.value = Number.isInteger(normalizedCount) && normalizedCount > 0 ? normalizedCount : 0
}

const onTaskTagsChanged = async () => {
  await fetchTask()
}

const enableTagsFeature = () => {
  isTagsFeatureEnabled.value = true
}

const createChecklistFromFeatureMenu = async () => {
  if (isTaskReadonly.value || hasChecklistFeature.value || isCreatingChecklistFromFeatureMenu.value) {
    return
  }

  isCreatingChecklistFromFeatureMenu.value = true

  try {
    const checklist = await checklistsCard.value?.createChecklistFromFeatureMenu?.()

    if (checklist?.id) {
      checklistCount.value = Math.max(checklistCount.value, 1)
      await fetchTask()
    }
  } catch (error) {
    toast.error(error?.response?.data?.message ?? error?.message ?? 'Unable to create checklist.')
  } finally {
    isCreatingChecklistFromFeatureMenu.value = false
  }
}

const normalizeUserId = (value) => {
  const userId = Number(value)

  return Number.isInteger(userId) && userId > 0 ? userId : null
}
</script>
